import React, { useEffect, useState, useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useNavigate, Link } from "react-router-dom";
import {
    LogOut,
    DollarSign,
    Mail,
    Users,
    ShieldCheck,
    Coffee,
    Inbox,
    ChevronRight,
    Check,
    X as XIcon,
    Copy,
    Search,
    PackageCheck,
    Clock,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "orders", label: "Orders" },
    { id: "abandoned", label: "Abandoned Carts" },
    { id: "wholesale", label: "Wholesale" },
    { id: "contacts", label: "Contacts" },
    { id: "newsletter", label: "Newsletter" },
    { id: "skus", label: "SKUs" },
];

const TAB_TO_PATH = {
    orders: "/admin/orders",
    abandoned: "/admin/abandoned-carts",
    wholesale: "/admin/wholesale",
    contacts: "/admin/contacts",
    newsletter: "/admin/newsletter",
    skus: "/admin/skus",
};

// Table header arrays — hoisted to module scope so identity is stable across renders
const ORDERS_HEADERS = ["Date", "Customer", "Items", "Amount", "Status"];
const WHOLESALE_HEADERS = ["Date", "Applicant", "Role", "Department", "Status", "Actions"];
const CONTACTS_HEADERS = ["Date", "From", "Subject", "Message"];
const NEWSLETTER_HEADERS = ["Date", "Email"];
const ABANDONED_HEADERS = ["Date", "Customer", "Items", "Amount", "Email Sent", "Recovered"];
const SKUS_HEADERS = ["SKU", "UPC", "Product", "Roast", "Size", "Grind", "Price"];

const Admin = () => {
    const { isAuthed, logout, adminGet, adminPatch, adminPost } = useAdmin();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const [stats, setStats] = useState(null);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);

    const refreshStats = useCallback(() => {
        return adminGet("/admin/stats")
            .then(setStats)
            .catch(() => toast.error("Failed to load stats"));
    }, [adminGet]);

    useEffect(() => {
        if (!isAuthed) navigate("/admin/login");
    }, [isAuthed, navigate]);

    useEffect(() => {
        if (!isAuthed) return;
        refreshStats();
    }, [isAuthed, refreshStats]);

    useEffect(() => {
        if (!isAuthed || tab === "overview") return;
        const path = TAB_TO_PATH[tab];
        if (!path) return;
        setLoading(true);
        adminGet(path)
            .then((d) => setData((prev) => ({ ...prev, [tab]: d })))
            .catch(() => toast.error(`Failed to load ${tab}`))
            .finally(() => setLoading(false));
    }, [tab, isAuthed, adminGet]);

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const updateWholesale = async (id, status) => {
        try {
            await adminPatch(`/admin/wholesale/${id}`, { status });
            toast.success(`Marked as ${status}`);
            const fresh = await adminGet("/admin/wholesale");
            setData((prev) => ({ ...prev, wholesale: fresh }));
        } catch (e) {
            toast.error("Failed to update");
        }
    };

    const releasePreorders = async () => {
        const held = stats?.preorders_held || 0;
        if (held === 0) {
            toast.info("No held pre-orders to release.");
            return;
        }
        const ok = window.confirm(
            `Release ${held} held pre-order${held === 1 ? "" : "s"} to ShipStation for fulfillment? This cannot be undone.`,
        );
        if (!ok) return;
        try {
            const result = await adminPost("/admin/preorders/release");
            toast.success(
                `Released ${result.pushed || 0} order${(result.pushed || 0) === 1 ? "" : "s"} to ShipStation` +
                    (result.skipped_missing_address
                        ? ` · ${result.skipped_missing_address} skipped (missing address)`
                        : "") +
                    (result.failed ? ` · ${result.failed} failed` : ""),
            );
            await refreshStats();
            // Refresh orders tab data if user has it cached
            if (data.orders) {
                const fresh = await adminGet("/admin/orders");
                setData((prev) => ({ ...prev, orders: fresh }));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to release pre-orders");
        }
    };

    if (!isAuthed) return null;

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#0B0E14] text-white">
            {/* Top bar */}
            <header className="border-b border-[#1E293B] sticky top-0 z-30 bg-[#0B0E14]/95 backdrop-blur">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="font-anton text-lg uppercase tracking-wider">
                            Badge & Shield
                        </div>
                        <div className="font-chivo text-[10px] tracking-[0.28em] uppercase text-[#004466]">
                            Admin
                        </div>
                    </Link>
                    <button
                        onClick={handleLogout}
                        data-testid="admin-logout-button"
                        className="btn-ghost flex items-center gap-2"
                    >
                        <LogOut className="w-4 h-4" />
                        Logout
                    </button>
                </div>
            </header>

            {/* Tabs */}
            <nav className="border-b border-[#1E293B] bg-[#0B0E14]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex gap-1 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-4 font-chivo text-xs tracking-[0.2em] uppercase whitespace-nowrap border-b-2 transition-colors ${
                                tab === t.id
                                    ? "border-[#004466] text-white"
                                    : "border-transparent text-slate-400 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </nav>

            <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
                {tab === "overview" && (
                    <Overview stats={stats} onReleasePreorders={releasePreorders} />
                )}
                {tab === "orders" && (
                    <OrdersTable data={data.orders} loading={loading} />
                )}
                {tab === "abandoned" && (
                    <AbandonedCartsPanel data={data.abandoned} loading={loading} />
                )}
                {tab === "skus" && (
                    <SkusPanel data={data.skus} loading={loading} />
                )}
                {tab === "wholesale" && (
                    <WholesaleTable
                        data={data.wholesale}
                        loading={loading}
                        onUpdate={updateWholesale}
                    />
                )}
                {tab === "contacts" && (
                    <ContactsTable data={data.contacts} loading={loading} />
                )}
                {tab === "newsletter" && (
                    <NewsletterTable data={data.newsletter} loading={loading} />
                )}
            </main>
        </div>
    );
};

// =========================================================================
// Stat / table components
// =========================================================================
const StatCard = ({ icon: Icon, label, value, sub }) => (
    <div className="bs-card p-6">
        <div className="flex items-center justify-between mb-4">
            <Icon className="w-5 h-5 text-[#004466]" />
            <ChevronRight className="w-4 h-4 text-slate-600" />
        </div>
        <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
            {label}
        </div>
        <div className="font-anton text-3xl md:text-4xl text-white mt-1">
            {value}
        </div>
        {sub && (
            <div className="font-body text-xs text-slate-500 mt-1">{sub}</div>
        )}
    </div>
);

const Overview = ({ stats, onReleasePreorders }) => {
    if (!stats)
        return <div className="text-slate-400 font-body">Loading…</div>;

    const held = stats.preorders_held || 0;
    const totalPre = stats.preorders_total || 0;
    const phase = stats.shop_phase || "live";
    const phaseLabel =
        phase === "preorder"
            ? "Pre-order Window Active"
            : phase === "maintenance"
              ? "Maintenance Mode"
              : phase === "coming_soon"
                ? "Coming Soon"
                : "Live";
    const phaseColor =
        phase === "preorder"
            ? "text-[#eab308]"
            : phase === "live"
              ? "text-[#22c55e]"
              : "text-slate-400";

    return (
        <div data-testid="admin-overview" className="space-y-10">
            <div>
                <h2 className="font-anton text-3xl md:text-4xl uppercase tracking-wide mb-2">
                    Operational <span className="text-[#004466]">Status</span>
                </h2>
                <div className={`font-chivo text-[11px] tracking-[0.24em] uppercase ${phaseColor}`}>
                    {phaseLabel}
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard
                    icon={DollarSign}
                    label="Revenue (paid)"
                    value={`$${stats.revenue_total.toFixed(2)}`}
                    sub={`${stats.orders_total} orders`}
                />
                <StatCard
                    icon={ShieldCheck}
                    label="Wholesale Apps"
                    value={stats.wholesale_total}
                    sub={`${stats.wholesale_pending} pending`}
                />
                <StatCard
                    icon={Mail}
                    label="Newsletter"
                    value={stats.newsletter_total}
                    sub="subscribers"
                />
                <StatCard
                    icon={Inbox}
                    label="Contact Forms"
                    value={stats.contacts_total}
                />
                <StatCard
                    icon={Users}
                    label="Orders"
                    value={stats.orders_total}
                />
            </div>

            {/* Pre-order command panel */}
            <div
                data-testid="admin-preorder-panel"
                className="bs-card p-6 md:p-8 border-l-2 border-l-[#eab308]/60"
            >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                            <Clock className="w-5 h-5 text-[#eab308]" />
                            <h3 className="font-anton text-xl md:text-2xl uppercase tracking-wide">
                                Pre-order <span className="text-[#eab308]">Batch</span>
                            </h3>
                        </div>
                        <p className="text-slate-400 text-sm font-body max-w-2xl mb-4">
                            June pre-orders are paid in full and held back from ShipStation.
                            On launch day (July 1), click below to push every held order into
                            fulfillment in one batch.
                        </p>
                        <div className="flex flex-wrap gap-6">
                            <div>
                                <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                                    Held (not yet pushed)
                                </div>
                                <div className="font-anton text-3xl text-[#eab308] mt-1">
                                    {held}
                                </div>
                            </div>
                            <div>
                                <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                                    Total Pre-orders
                                </div>
                                <div className="font-anton text-3xl text-white mt-1">
                                    {totalPre}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="release-preorders-button"
                        onClick={onReleasePreorders}
                        disabled={held === 0}
                        className="btn-primary inline-flex items-center gap-2 self-start lg:self-center disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <PackageCheck className="w-4 h-4" />
                        Release {held} Pre-order{held === 1 ? "" : "s"} to ShipStation
                    </button>
                </div>

                {/* Auto-release status footer */}
                <AutoReleaseStatus auto={stats.auto_release} />
            </div>
        </div>
    );
};

const AutoReleaseStatus = ({ auto }) => {
    if (!auto) return null;
    const launch = auto.launch_date ? new Date(auto.launch_date) : null;
    const launchLabel = launch ? launch.toLocaleString() : "—";
    const released = !!auto.released_at;
    const releasedLabel = released ? new Date(auto.released_at).toLocaleString() : null;

    return (
        <div
            data-testid="auto-release-status"
            className="mt-6 pt-6 border-t border-[#1E293B] flex flex-col sm:flex-row sm:items-center gap-3 text-xs font-body"
        >
            <span className="font-chivo text-[10px] tracking-[0.24em] uppercase text-slate-500">
                Auto-release
            </span>
            {!auto.enabled ? (
                <span className="text-slate-500">Disabled (env override)</span>
            ) : released ? (
                <span className="text-[#22c55e]">
                    ✓ Fired {releasedLabel} ·{" "}
                    <span className="text-slate-400">
                        pushed {auto.result?.pushed ?? 0}
                        {auto.result?.skipped_missing_address
                            ? ` · skipped ${auto.result.skipped_missing_address}`
                            : ""}
                        {auto.result?.failed ? ` · failed ${auto.result.failed}` : ""}
                    </span>
                </span>
            ) : (
                <span className="text-slate-400">
                    Armed — will auto-fire at{" "}
                    <span className="text-white font-mono">{launchLabel}</span>
                </span>
            )}
        </div>
    );
};

const TableShell = ({ headers, children, empty, loading, testid }) => {
    if (loading)
        return <div className="text-slate-400 font-body py-8">Loading…</div>;
    return (
        <div data-testid={testid} className="overflow-x-auto bs-card">
            <table className="w-full text-sm">
                <thead className="border-b border-[#1E293B]">
                    <tr>
                        {headers.map((h) => (
                            <th
                                key={h}
                                className="text-left px-4 py-3 font-chivo text-[10px] tracking-[0.22em] uppercase text-[#004466]"
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {React.Children.count(children) === 0 ? (
                        <tr>
                            <td
                                colSpan={headers.length}
                                className="px-4 py-12 text-center text-slate-500 font-body"
                            >
                                {empty || "No records"}
                            </td>
                        </tr>
                    ) : (
                        children
                    )}
                </tbody>
            </table>
        </div>
    );
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const OrdersTable = ({ data, loading }) => (
    <TableShell
        loading={loading}
        testid="admin-orders-table"
        headers={ORDERS_HEADERS}
        empty="No orders yet"
    >
        {(data || []).map((o) => (
            <tr key={o.id} className="border-b border-[#1E293B] hover:bg-[#1E2530]/40">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3">
                    <div className="text-white">{o.customer_name || "—"}</div>
                    <div className="text-xs text-slate-500">{o.customer_email || ""}</div>
                </td>
                <td className="px-4 py-3 text-slate-300 max-w-md">
                    {(o.items || [])
                        .map((i) => `${i.name} ×${i.quantity}`)
                        .join(", ")}
                </td>
                <td className="px-4 py-3 font-anton text-lg text-[#ffffff]">
                    ${(o.amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3">
                    <span className="inline-block px-2 py-1 bg-[#004466]/20 text-[#3b82f6] font-chivo text-[10px] tracking-[0.18em] uppercase">
                        {o.status}
                    </span>
                </td>
            </tr>
        ))}
    </TableShell>
);

const WholesaleTable = ({ data, loading, onUpdate }) => (
    <TableShell
        loading={loading}
        testid="admin-wholesale-table"
        headers={WHOLESALE_HEADERS}
        empty="No wholesale applications"
    >
        {(data || []).map((w) => (
            <tr key={w.id} className="border-b border-[#1E293B] hover:bg-[#1E2530]/40">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(w.created_at)}</td>
                <td className="px-4 py-3">
                    <div className="text-white">{w.name}</div>
                    <div className="text-xs text-slate-500">{w.email} · {w.phone || "—"}</div>
                </td>
                <td className="px-4 py-3 text-slate-300 uppercase font-chivo text-xs tracking-[0.18em]">{w.role}</td>
                <td className="px-4 py-3 text-slate-300">
                    {w.department || "—"}
                    {w.location && (
                        <div className="text-xs text-slate-500">{w.location}</div>
                    )}
                </td>
                <td className="px-4 py-3">
                    <StatusPill status={w.status} />
                </td>
                <td className="px-4 py-3">
                    <div className="flex gap-2">
                        <button
                            data-testid={`approve-wholesale-${w.id}`}
                            onClick={() => onUpdate(w.id, "approved")}
                            disabled={w.status === "approved"}
                            className="p-2 border border-[#1E293B] hover:border-[#004466] disabled:opacity-30"
                            title="Approve"
                        >
                            <Check className="w-4 h-4 text-[#22c55e]" />
                        </button>
                        <button
                            data-testid={`reject-wholesale-${w.id}`}
                            onClick={() => onUpdate(w.id, "rejected")}
                            disabled={w.status === "rejected"}
                            className="p-2 border border-[#1E293B] hover:border-[#004466] disabled:opacity-30"
                            title="Reject"
                        >
                            <XIcon className="w-4 h-4 text-[#ef4444]" />
                        </button>
                    </div>
                </td>
            </tr>
        ))}
    </TableShell>
);

const ContactsTable = ({ data, loading }) => (
    <TableShell
        loading={loading}
        testid="admin-contacts-table"
        headers={CONTACTS_HEADERS}
        empty="No contact submissions"
    >
        {(data || []).map((c) => (
            <tr key={c.id} className="border-b border-[#1E293B] hover:bg-[#1E2530]/40 align-top">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3">
                    <div className="text-white">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                    {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                </td>
                <td className="px-4 py-3 text-slate-300">{c.subject || "—"}</td>
                <td className="px-4 py-3 text-slate-300 max-w-xl whitespace-pre-line">{c.message}</td>
            </tr>
        ))}
    </TableShell>
);

const NewsletterTable = ({ data, loading }) => (
    <TableShell
        loading={loading}
        testid="admin-newsletter-table"
        headers={NEWSLETTER_HEADERS}
        empty="No newsletter subscribers yet"
    >
        {(data || []).map((n) => (
            <tr key={n.id} className="border-b border-[#1E293B] hover:bg-[#1E2530]/40">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(n.created_at)}</td>
                <td className="px-4 py-3 text-white font-mono text-sm">{n.email}</td>
            </tr>
        ))}
    </TableShell>
);

const AbandonedCartsPanel = ({ data, loading }) => {
    if (loading) return <div className="text-slate-400 font-body py-8">Loading…</div>;
    const items = data?.items || [];
    const stats = data?.stats || {};

    return (
        <div data-testid="admin-abandoned-panel" className="space-y-8">
            <div>
                <h2 className="font-anton text-2xl md:text-3xl uppercase tracking-wide mb-2">
                    Abandoned <span className="text-[#004466]">Carts</span>
                </h2>
                <p className="text-slate-400 text-sm font-body max-w-2xl">
                    Checkouts that expired before payment. Customers with stored emails
                    automatically receive a recovery email with your <span className="font-mono text-white">COMEBACK10</span> code.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard
                    icon={Inbox}
                    label="Total Abandoned"
                    value={stats.total_abandoned || 0}
                />
                <StatCard
                    icon={Mail}
                    label="Recovery Emails Sent"
                    value={stats.email_sent_count || 0}
                    sub={stats.total_abandoned ? `${Math.round((stats.email_sent_count || 0) / stats.total_abandoned * 100)}% of abandoned` : "—"}
                />
                <StatCard
                    icon={ShieldCheck}
                    label="Recovered"
                    value={stats.recovered_count || 0}
                    sub={`${stats.recovery_rate || 0}% recovery rate`}
                />
                <StatCard
                    icon={DollarSign}
                    label="Lost Cart Value"
                    value={`$${(stats.abandoned_value || 0).toFixed(2)}`}
                />
            </div>

            <TableShell
                loading={false}
                testid="admin-abandoned-table"
                headers={ABANDONED_HEADERS}
                empty="No abandoned carts yet — nice conversion rate."
            >
                {items.map((a) => (
                    <tr key={a.session_id} className="border-b border-[#1E293B] hover:bg-[#1E2530]/40 align-top">
                        <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                        <td className="px-4 py-3">
                            <div className="text-white">{a.customer_name || "—"}</div>
                            <div className="text-xs text-slate-500">{a.customer_email || "no email captured"}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300 max-w-md text-xs">
                            {(a.items || [])
                                .map((i) => `${i.name} ×${i.quantity}`)
                                .join(", ") || "—"}
                        </td>
                        <td className="px-4 py-3 font-anton text-lg text-white">
                            ${(a.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                            {a.abandoned_email_sent ? (
                                <span className="inline-block px-2 py-1 bg-[#22c55e]/15 text-[#22c55e] font-chivo text-[10px] tracking-[0.18em] uppercase">
                                    Sent
                                </span>
                            ) : (
                                <span className="inline-block px-2 py-1 bg-slate-500/15 text-slate-400 font-chivo text-[10px] tracking-[0.18em] uppercase">
                                    —
                                </span>
                            )}
                        </td>
                        <td className="px-4 py-3">
                            {a.recovered ? (
                                <span className="inline-block px-2 py-1 bg-[#22c55e]/15 text-[#22c55e] font-chivo text-[10px] tracking-[0.18em] uppercase">
                                    Yes
                                </span>
                            ) : (
                                <span className="inline-block px-2 py-1 bg-[#ef4444]/10 text-slate-400 font-chivo text-[10px] tracking-[0.18em] uppercase">
                                    No
                                </span>
                            )}
                        </td>
                    </tr>
                ))}
            </TableShell>
        </div>
    );
};

const SkusPanel = ({ data, loading }) => {
    const [filter, setFilter] = React.useState("");
    if (loading) return <div className="text-slate-400 font-body py-8">Loading…</div>;
    const items = data?.items || [];
    const total = data?.total || 0;

    const q = filter.toLowerCase().trim();
    const filtered = q
        ? items.filter(
              (i) =>
                  i.sku.toLowerCase().includes(q) ||
                  (i.upc || "").toLowerCase().includes(q) ||
                  i.product_name.toLowerCase().includes(q) ||
                  i.size.toLowerCase().includes(q) ||
                  i.grind.toLowerCase().includes(q),
          )
        : items;

    const copy = (value) => {
        if (!value) return;
        navigator.clipboard
            .writeText(value)
            .then(() => toast.success(`Copied ${value}`))
            .catch(() => toast.error("Couldn't copy"));
    };

    const exportCsv = () => {
        const header = "SKU,UPC,Product,Roast,Size,Grind,Price (USD),Variant ID";
        const lines = items.map(
            (i) =>
                `${i.sku},${i.upc || ""},"${i.product_name}",${i.roast},${i.size},${i.grind},${i.price.toFixed(2)},${i.variant_id}`,
        );
        const csv = [header, ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "badge-shield-skus.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-skus-panel" className="space-y-8">
            <div>
                <h2 className="font-anton text-2xl md:text-3xl uppercase tracking-wide mb-2">
                    SKU <span className="text-[#004466]">Catalog</span>
                </h2>
                <p className="text-slate-400 text-sm font-body max-w-2xl">
                    Format: <span className="font-mono text-white">BSC-{"{PRODUCT}"}-{"{SIZE}"}-{"{GRIND}"}</span>.
                    Click any SKU to copy. Use these on bag labels, in your inventory system, or anywhere you need a
                    unique identifier.
                </p>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <LegendCard title="Products" entries={data?.legend?.products || {}} mapKey="id" />
                <LegendCard title="Sizes" entries={data?.legend?.sizes || {}} mapKey="size" />
                <LegendCard title="Grinds" entries={data?.legend?.grinds || {}} mapKey="grind" />
            </div>

            {/* Search + Export */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        data-testid="sku-search-input"
                        placeholder="Filter by SKU, UPC, product, size, or grind…"
                        className="bs-input w-full pl-10"
                    />
                </div>
                <button
                    onClick={exportCsv}
                    data-testid="sku-export-csv-button"
                    className="btn-secondary inline-flex justify-center"
                >
                    Export CSV
                </button>
            </div>

            <TableShell
                loading={false}
                testid="admin-skus-table"
                headers={SKUS_HEADERS}
                empty={`No SKUs match "${filter}".`}
            >
                {filtered.map((row) => (
                    <tr key={row.sku} className="border-b border-[#1E293B] hover:bg-[#1E2530]/40">
                        <td className="px-4 py-3">
                            <button
                                onClick={() => copy(row.sku)}
                                data-testid={`sku-copy-${row.sku}`}
                                className="flex items-center gap-2 font-mono text-white hover:text-[#3b82f6] transition-colors group"
                                title="Click to copy"
                            >
                                <span>{row.sku}</span>
                                <Copy className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                            </button>
                        </td>
                        <td className="px-4 py-3">
                            {row.upc ? (
                                <button
                                    onClick={() => copy(row.upc)}
                                    data-testid={`upc-copy-${row.sku}`}
                                    className="flex items-center gap-2 font-mono text-slate-300 hover:text-[#3b82f6] transition-colors group text-sm"
                                    title="Click to copy UPC"
                                >
                                    <span>{row.upc}</span>
                                    <Copy className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                                </button>
                            ) : (
                                <span className="text-slate-600 text-xs font-mono">—</span>
                            )}
                        </td>
                        <td className="px-4 py-3 text-slate-300">{row.product_name}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{row.roast}</td>
                        <td className="px-4 py-3 text-slate-300">{row.size}</td>
                        <td className="px-4 py-3 text-slate-300">{row.grind}</td>
                        <td className="px-4 py-3 font-anton text-lg text-white">
                            ${row.price.toFixed(2)}
                        </td>
                    </tr>
                ))}
            </TableShell>

            <div className="text-xs text-slate-500 font-mono">
                Showing {filtered.length} of {total} SKUs
            </div>
        </div>
    );
};

const LegendCard = ({ title, entries }) => (
    <div className="bg-[#0F141C] border border-[#1E293B] p-4">
        <div className="font-chivo text-[10px] tracking-[0.22em] uppercase text-[#3b82f6] mb-3">
            {title}
        </div>
        <div className="space-y-1.5">
            {Object.entries(entries).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs gap-3">
                    <span className="text-slate-400 truncate">{k}</span>
                    <span className="font-mono text-white">{v}</span>
                </div>
            ))}
        </div>
    </div>
);

const StatusPill = ({ status }) => {
    const map = {
        paid: "bg-[#22c55e]/15 text-[#22c55e]",
        active: "bg-[#22c55e]/15 text-[#22c55e]",
        pending: "bg-[#eab308]/15 text-[#eab308]",
        initiated: "bg-[#eab308]/15 text-[#eab308]",
        approved: "bg-[#22c55e]/15 text-[#22c55e]",
        rejected: "bg-[#ef4444]/15 text-[#ef4444]",
    };
    const cls = map[status] || "bg-slate-500/15 text-slate-300";
    return (
        <span
            className={`inline-block px-2 py-1 font-chivo text-[10px] tracking-[0.18em] uppercase ${cls}`}
        >
            {status || "—"}
        </span>
    );
};

export default Admin;
